import Cookies from "js-cookie";

/**
 * Retrieves the value of a specific query parameter from the current page URL.
 *
 * @function get_query_param
 * @param {string} name - The name of the query parameter to retrieve.
 * @returns {string | null} The value of the query parameter if found, otherwise `null`.
 *
 * @example
 * // URL: https://example.com/?utm_source=google
 * const source = get_query_param("utm_source");
 * console.log(source); // "google"
 */
export const get_query_param = (name: string): string | null => {
  return new URLSearchParams(window.location.search).get(name);
};

/**
 * Sets a cookie with a given name and value.
 *
 * - If `days` is provided, the cookie will persist for that number of days.
 * - If `days` is omitted, it will be a session cookie (expires when the browser closes).
 * - The cookie is always first-party (no domain is set).
 *
 * @function set_cookie
 * @param {string} name - The name of the cookie to set.
 * @param {string} value - The value to store in the cookie.
 * @param {number} [days] - Optional. The number of days before the cookie expires.
 * @returns {void}
 *
 * @example
 * // Persistent cookie (7 days)
 * set_cookie("user_id", "12345", 7);
 *
 * // Session cookie
 * set_cookie("session_token", "abcdef");
 */
export const set_cookie = (
  name: string,
  value: string,
  days?: number,
): void => {
  const options: Cookies.CookieAttributes = {
    path: "/",
    sameSite: "Lax",
    secure: window.location.protocol === "https:",
  };

  if (days !== undefined) {
    options.expires = days;
  }

  Cookies.set(name, value, options);
};

/**
 * Retrieves the value of a cookie by its name.
 *
 * @function get_cookie
 * @param {string} name - The name of the cookie to retrieve.
 * @returns {string | null} The cookie value if found, otherwise `null`.
 *
 * @example
 * const token = get_cookie("session_token");
 * if (token) {
 *   console.log("Token:", token);
 * } else {
 *   console.log("No session token found.");
 * }
 */
export const get_cookie = (name: string): string | null => {
  const value = Cookies.get(name);
  return value !== undefined ? value : null;
};

/**
 * Deletes a cookie by its name.
 *
 * @function destroy_cookie
 * @param {string} name - The name of the cookie to delete.
 * @returns {void}
 *
 * @example
 * destroy_cookie("session_token");
 */
export const destroy_cookie = (name: string): void => {
  const options: Cookies.CookieAttributes = {
    path: "/",
    sameSite: "Lax",
    secure: window.location.protocol === "https:",
  };

  Cookies.remove(name, options);
};
